import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  CpuChipIcon,
  CircleStackIcon,
  ServerIcon,
  CloudIcon,
  SignalIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  BoltIcon,
  WifiIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CogIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon,
  ShieldExclamationIcon,
  UserIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../utils/axios';
import { format } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const SystemHealthDetail = ({ isOpen, onClose }) => {
  const [healthData, setHealthData] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [historicalData, setHistoricalData] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchHealthData();
      fetchDetailedData();
      // Simulate historical data - in real app, fetch from API
      generateHistoricalData();
      
      // Set up polling for network tab to update traffic rates
      if (activeTab === 'network') {
        const interval = setInterval(() => {
          fetchHealthData();
        }, 5000); // Update every 5 seconds
        
        return () => clearInterval(interval);
      }
    }
  }, [isOpen, activeTab]);

  const fetchHealthData = async () => {
    try {
      const response = await axiosInstance.get('/system-health');
      if (response.data.success) {
        setHealthData(response.data);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch system health:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedData = async () => {
    try {
      const response = await axiosInstance.get('/system-health/detailed');
      if (response.data.success) {
        setDetailedData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch detailed health:', err);
      // Non-critical, don't set error
    }
  };

  const generateHistoricalData = () => {
    const data = [];
    const now = Date.now();
    for (let i = 29; i >= 0; i--) {
      data.push({
        time: format(new Date(now - i * 60000), 'HH:mm'),
        cpu: Math.floor(Math.random() * 30 + 20),
        memory: Math.floor(Math.random() * 20 + 40),
        networkIn: Math.floor(Math.random() * 100 + 50),
        networkOut: Math.floor(Math.random() * 80 + 20)
      });
    }
    setHistoricalData(data);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'aws', label: 'AWS', icon: CloudIcon },
    { id: 'system', label: 'System', icon: ServerIcon },
    { id: 'applications', label: 'Applications', icon: ServerIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'application', label: 'Database', icon: CircleStackIcon },
    { id: 'network', label: 'Network', icon: WifiIcon }
  ];

  const getHealthColor = (status) => {
    const colors = {
      excellent: 'text-green-600 bg-green-50',
      good: 'text-blue-600 bg-blue-50',
      fair: 'text-yellow-600 bg-yellow-50',
      poor: 'text-orange-600 bg-orange-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors[status] || colors.fair;
  };

  const renderTabContent = () => {
    if (!healthData) return null;

    switch (activeTab) {
      case 'overview':
        return <OverviewTab healthData={healthData} historicalData={historicalData} />;
      case 'aws':
        return <AWSTab aws={healthData.aws} />;
      case 'system':
        return <SystemTab system={healthData.system} detailedData={detailedData} />;
      case 'applications':
        return <ApplicationsTab applications={healthData.applications} />;
      case 'security':
        return <SecurityTab security={healthData.security} />;
      case 'application':
        return <ApplicationTab application={healthData.application} storage={healthData.system?.storage} />;
      case 'network':
        return <NetworkTab system={healthData.system} aws={healthData.aws} />;
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 overflow-hidden"
        >
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
          
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 bottom-0 w-full max-w-7xl bg-white shadow-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">System Health Details</h2>
                {healthData && (
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getHealthColor(healthData.health?.status)}`}>
                      {healthData.health?.status?.toUpperCase()} - Score: {healthData.health?.score}/100
                    </span>
                    <span className="text-sm text-gray-500">
                      Updated: {format(new Date(healthData.timestamp), 'PPp')}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    fetchHealthData();
                    fetchDetailedData();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowPathIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex space-x-8 px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 py-3 px-1 border-b-2 transition-colors
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                      }
                    `}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100% - 180px)' }}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading system health data...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto" />
                    <p className="mt-4 text-gray-700">Failed to load system health data</p>
                    <p className="text-sm text-gray-500">{error}</p>
                    <button
                      onClick={fetchHealthData}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  {renderTabContent()}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Overview Tab Component
const OverviewTab = ({ healthData, historicalData }) => {
  const { summary, system } = healthData;
  
  const pieData = [
    { name: 'Used Memory', value: parseInt(summary?.memory) || 0 },
    { name: 'Free Memory', value: 100 - (parseInt(summary?.memory) || 0) }
  ];
  
  const COLORS = ['#3B82F6', '#E5E7EB'];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="CPU Usage"
          value={summary?.cpu}
          icon={CpuChipIcon}
          color="blue"
          trend={5}
        />
        <MetricCard
          title="Memory Usage"
          value={summary?.memory}
          icon={CircleStackIcon}
          color="purple"
          trend={-2}
        />
        <MetricCard
          title="System Load"
          value={`${system?.load?.['5min'] || 'N/A'}`}
          icon={BoltIcon}
          color="yellow"
          suffix=""
        />
        <MetricCard
          title="Network Activity"
          value={summary?.network?.primaryInterface ? 
            `${summary.network.primaryInterface.rx_rate || '0 B/s'}` : 'N/A'}
          icon={WifiIcon}
          color="green"
          suffix=""
        />
      </div>

      {/* Performance Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={historicalData}>
            <defs>
              <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="time" stroke="#6B7280" fontSize={12} />
            <YAxis stroke="#6B7280" fontSize={12} />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="cpu"
              stroke="#3B82F6"
              fillOpacity={1}
              fill="url(#colorCpu)"
              name="CPU %"
            />
            <Area
              type="monotone"
              dataKey="memory"
              stroke="#8B5CF6"
              fillOpacity={1}
              fill="url(#colorMemory)"
              name="Memory %"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {pieData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span className="text-sm text-gray-600">Used</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-200 rounded"></div>
              <span className="text-sm text-gray-600">Free</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Info</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Platform</dt>
              <dd className="text-sm font-medium text-gray-900">{system?.system?.platform}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Architecture</dt>
              <dd className="text-sm font-medium text-gray-900">{system?.system?.arch}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Node Version</dt>
              <dd className="text-sm font-medium text-gray-900">{system?.system?.nodeVersion}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Uptime</dt>
              <dd className="text-sm font-medium text-gray-900">{summary?.uptime}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Active Connections</dt>
              <dd className="text-sm font-medium text-gray-900">
                {(summary?.connections?.devices || 0) + (summary?.connections?.users || 0)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

// AWS Tab Component
const AWSTab = ({ aws }) => {
  if (!aws?.metadata) {
    return (
      <div className="text-center py-12">
        <CloudIcon className="w-12 h-12 text-gray-400 mx-auto" />
        <p className="mt-4 text-gray-500">No AWS metadata available</p>
        <p className="text-sm text-gray-400 mt-2">This might not be an AWS instance</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Instance Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">EC2 Instance Information</h3>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Instance ID" value={aws.metadata.instanceId} />
          <InfoItem label="Instance Type" value={aws.metadata.instanceType} />
          <InfoItem label="Region" value={aws.metadata.region} />
          <InfoItem label="Availability Zone" value={aws.metadata.availabilityZone} />
          <InfoItem label="Public IP" value={aws.metadata.publicIp || 'N/A'} />
          <InfoItem label="Private IP" value={aws.metadata.privateIp} />
        </dl>
      </div>

      {/* CloudWatch Metrics */}
      {aws.cloudWatch && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CloudWatch Metrics (Last 5 min)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricDisplay
              label="CPU Utilization"
              value={aws.cloudWatch.cpu?.toFixed(2)}
              unit="%"
              icon={CpuChipIcon}
            />
            <MetricDisplay
              label="Network In"
              value={formatBytes(aws.cloudWatch.network_in)}
              icon={ArrowDownIcon}
            />
            <MetricDisplay
              label="Network Out"
              value={formatBytes(aws.cloudWatch.network_out)}
              icon={ArrowUpIcon}
            />
          </div>
        </div>
      )}

      {/* Instance Type Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Instance Specifications</h3>
        <InstanceTypeInfo instanceType={aws.metadata.instanceType} specifications={aws.metadata.specifications} />
      </div>
    </div>
  );
};

// System Tab Component
const SystemTab = ({ system, detailedData }) => {
  return (
    <div className="space-y-6">
      {/* CPU Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">CPU Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <dl className="space-y-2">
              <InfoItem label="Model" value={system?.cpu?.model} />
              <InfoItem label="Cores" value={system?.cpu?.count} />
              <InfoItem label="Speed" value={`${system?.cpu?.speed} MHz`} />
              <InfoItem label="Usage" value={`${system?.cpu?.usage}%`} />
            </dl>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Load Average</h4>
            <div className="space-y-2">
              <LoadBar label="1 min" value={parseFloat(system?.load?.normalized?.['1min'])} />
              <LoadBar label="5 min" value={parseFloat(system?.load?.normalized?.['5min'])} />
              <LoadBar label="15 min" value={parseFloat(system?.load?.normalized?.['15min'])} />
            </div>
          </div>
        </div>
      </div>

      {/* Memory Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Total" value={`${system?.memory?.totalGB} GB`} />
          <StatCard label="Used" value={`${system?.memory?.usedGB} GB`} color="yellow" />
          <StatCard label="Free" value={`${system?.memory?.freeGB} GB`} color="green" />
        </div>
        <div className="mt-4">
          <ProgressBar value={system?.memory?.usage} label="Memory Usage" />
        </div>
      </div>

      {/* Disk Information */}
      {system?.disk && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Disk Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label="Total" value={system.disk.total} />
            <StatCard label="Used" value={system.disk.used} color="yellow" />
            <StatCard label="Available" value={system.disk.available} color="green" />
            <StatCard label="Usage" value={`${system.disk.usage}%`} color="blue" />
          </div>
        </div>
      )}

      {/* Process Information */}
      {detailedData?.process && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Process Information</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoItem label="Process ID" value={detailedData.process.pid} />
            <InfoItem label="Process Uptime" value={formatUptime(detailedData.process.uptime)} />
            <InfoItem 
              label="Heap Used" 
              value={formatBytes(detailedData.process.memoryUsage.heapUsed)} 
            />
            <InfoItem 
              label="Heap Total" 
              value={formatBytes(detailedData.process.memoryUsage.heapTotal)} 
            />
          </dl>
        </div>
      )}
    </div>
  );
};

// Application Tab Component (Database Tab)
const ApplicationTab = ({ application, storage }) => {
  if (!application) {
    return (
      <div className="text-center py-12">
        <CogIcon className="w-12 h-12 text-gray-400 mx-auto" />
        <p className="mt-4 text-gray-500">No application data available</p>
      </div>
    );
  }

  // Helper to get alert colors
  const getAlertColors = (level) => {
    switch (level) {
      case 'critical':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500', icon: 'text-red-500' };
      case 'warning':
        return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', bar: 'bg-yellow-500', icon: 'text-yellow-500' };
      case 'info':
        return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', bar: 'bg-blue-500', icon: 'text-blue-500' };
      default:
        return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', bar: 'bg-green-500', icon: 'text-green-500' };
    }
  };

  return (
    <div className="space-y-6">
      {/* Storage Volumes Section */}
      {storage && storage.volumes && storage.volumes.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CircleStackIcon className="w-5 h-5 text-blue-600" />
              Storage Volumes
            </h3>
            {storage.summary && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  Total: {storage.summary.totalUsed?.formatted} / {storage.summary.totalSize?.formatted}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  storage.summary.overallAlert === 'critical' ? 'bg-red-100 text-red-700' :
                  storage.summary.overallAlert === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {storage.summary.overallUsage}% Used
                </span>
              </div>
            )}
          </div>

          {/* Storage Alert Banner */}
          {storage.summary?.overallAlert !== 'healthy' && (
            <div className={`mb-4 p-3 rounded-lg flex items-start gap-3 ${
              storage.summary.overallAlert === 'critical' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 ${
                storage.summary.overallAlert === 'critical' ? 'text-red-500' : 'text-yellow-500'
              }`} />
              <div>
                <p className={`font-medium ${storage.summary.overallAlert === 'critical' ? 'text-red-800' : 'text-yellow-800'}`}>
                  {storage.summary.overallAlert === 'critical' ? 'Critical Storage Alert' : 'Storage Warning'}
                </p>
                <p className={`text-sm ${storage.summary.overallAlert === 'critical' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {storage.volumes.filter(v => v.alert.level !== 'normal').map(v => v.alert.message).join(' ')}
                </p>
              </div>
            </div>
          )}

          {/* Volume Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {storage.volumes.map((volume, idx) => {
              const colors = getAlertColors(volume.alert.level);
              return (
                <div key={idx} className={`rounded-lg border p-4 ${colors.bg} ${colors.border}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {volume.icon === 'database' ? (
                        <CircleStackIcon className={`w-5 h-5 ${colors.icon}`} />
                      ) : (
                        <ServerIcon className={`w-5 h-5 ${colors.icon}`} />
                      )}
                      <div>
                        <h4 className="font-semibold text-gray-900">{volume.name}</h4>
                        <p className="text-xs text-gray-500">{volume.mountPoint}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      volume.alert.level === 'critical' ? 'bg-red-200 text-red-800' :
                      volume.alert.level === 'warning' ? 'bg-yellow-200 text-yellow-800' :
                      volume.alert.level === 'info' ? 'bg-blue-200 text-blue-800' :
                      'bg-green-200 text-green-800'
                    }`}>
                      {volume.usage}%
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${colors.bar}`}
                        style={{ width: `${volume.usage}%` }}
                      />
                    </div>
                  </div>

                  {/* Volume details */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white bg-opacity-50 rounded p-2">
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-sm font-semibold text-gray-900">{volume.size.formatted}</p>
                    </div>
                    <div className="bg-white bg-opacity-50 rounded p-2">
                      <p className="text-xs text-gray-500">Used</p>
                      <p className="text-sm font-semibold text-gray-900">{volume.used.formatted}</p>
                    </div>
                    <div className="bg-white bg-opacity-50 rounded p-2">
                      <p className="text-xs text-gray-500">Free</p>
                      <p className="text-sm font-semibold text-gray-900">{volume.available.formatted}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="mt-2 text-xs text-gray-600">{volume.description}</p>

                  {/* Alert message if any */}
                  {volume.alert.message && volume.alert.level !== 'normal' && (
                    <p className={`mt-2 text-xs font-medium ${colors.text}`}>
                      {volume.alert.message}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Database Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CircleStackIcon className="w-5 h-5 text-green-600" />
            Local MongoDB Database
          </h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            application.database?.connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {application.database?.connected ? '● Connected' : '○ Disconnected'}
          </span>
        </div>

        {/* Connection Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Type</p>
              <p className="text-sm font-semibold text-gray-900">{application.database?.type || 'MongoDB'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Host</p>
              <p className="text-sm font-semibold text-gray-900">{application.database?.host || 'localhost'}:{application.database?.port || 27017}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Database</p>
              <p className="text-sm font-semibold text-gray-900">{application.database?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Data Path</p>
              <p className="text-sm font-semibold text-gray-900">{application.database?.dataPath || '/data/mongodb'}</p>
            </div>
          </div>
        </div>

        {/* Server Info */}
        {application.database?.serverInfo && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">MongoDB Server</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-blue-50 rounded-lg p-4">
              <div>
                <p className="text-xs text-gray-500">Version</p>
                <p className="text-sm font-bold text-blue-700">{application.database.serverInfo.version}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Uptime</p>
                <p className="text-sm font-bold text-blue-700">{application.database.serverInfo.uptimeFormatted}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Connections</p>
                <p className="text-sm font-bold text-blue-700">
                  {application.database.serverInfo.connections?.current} / {application.database.serverInfo.connections?.available}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Memory (Resident)</p>
                <p className="text-sm font-bold text-blue-700">{application.database.serverInfo.memory?.resident}</p>
              </div>
            </div>
            {application.database.serverInfo.opcounters && (
              <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                <div className="bg-gray-100 rounded p-2">
                  <p className="text-xs text-gray-500">Inserts</p>
                  <p className="text-sm font-semibold">{application.database.serverInfo.opcounters.insert?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-100 rounded p-2">
                  <p className="text-xs text-gray-500">Queries</p>
                  <p className="text-sm font-semibold">{application.database.serverInfo.opcounters.query?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-100 rounded p-2">
                  <p className="text-xs text-gray-500">Updates</p>
                  <p className="text-sm font-semibold">{application.database.serverInfo.opcounters.update?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-100 rounded p-2">
                  <p className="text-xs text-gray-500">Deletes</p>
                  <p className="text-sm font-semibold">{application.database.serverInfo.opcounters.delete?.toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Database Stats */}
        {application.database?.stats && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Database Statistics</h4>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <InfoItem label="Collections" value={application.database.stats.collections} />
              <InfoItem label="Total Documents" value={application.database.stats.objects?.toLocaleString() || 'N/A'} />
              <InfoItem label="Data Size" value={application.database.stats.dataSize} />
              <InfoItem label="Storage Size" value={application.database.stats.storageSize} />
              <InfoItem label="Indexes" value={application.database.stats.indexes} />
              <InfoItem label="Avg Doc Size" value={application.database.stats.avgObjSize} />
            </dl>
          </div>
        )}
      </div>

      {/* Device & User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Statistics</h3>
          <div className="space-y-4">
            <StatRow label="Total Devices" value={application.devices?.total || 0} />
            <StatRow label="Online Devices" value={application.devices?.online || 0} color="green" />
            <StatRow label="Offline Devices" value={application.devices?.offline || 0} color="gray" />
            <StatRow label="Active in DB" value={application.devices?.activeInDb || 0} color="blue" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Statistics</h3>
          <div className="space-y-4">
            <StatRow label="Total Users" value={application.users?.total || 0} />
            <StatRow label="Active Users" value={application.users?.active || 0} color="green" />
            <StatRow label="Connected Users" value={application.users?.connected || 0} color="blue" />
            <StatRow label="Subscriptions" value={application.users?.subscriptions || 0} color="purple" />
          </div>
        </div>
      </div>

      {/* WebSocket Connections */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">WebSocket Connections</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricDisplay
            label="Device Connections"
            value={application.websocket?.deviceConnections || 0}
            icon={ServerIcon}
          />
          <MetricDisplay
            label="Subscriber Connections"
            value={application.websocket?.subscriberConnections || 0}
            icon={UserGroupIcon}
          />
          <MetricDisplay
            label="Total Subscriptions"
            value={application.websocket?.totalSubscriptions || 0}
            icon={SignalIcon}
          />
        </div>
      </div>

      {/* History Metrics */}
      {application.history && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">History Metrics (Last 24h)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard 
              label="Total Records" 
              value={application.history.last24Hours.toLocaleString()} 
            />
            <StatCard 
              label="Avg Per Hour" 
              value={application.history.avgPerHour.toLocaleString()} 
              color="blue"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Applications Tab Component
const ApplicationsTab = ({ applications }) => {
  if (!applications || applications.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <ServerIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No PM2 applications detected</p>
        </div>
      </div>
    );
  }

  const totalMemory = applications.reduce((sum, app) => sum + (app.memory || 0), 0);
  const totalCPU = applications.reduce((sum, app) => sum + (app.cpu || 0), 0);
  const onlineApps = applications.filter(app => app.status === 'online').length;

  return (
    <div className="p-6 space-y-6 overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          label="Total Applications" 
          value={applications.length} 
          color="blue"
        />
        <StatCard 
          label="Online" 
          value={onlineApps} 
          color="green"
        />
        <StatCard 
          label="Total CPU" 
          value={`${totalCPU.toFixed(1)}%`} 
          color={totalCPU > 80 ? 'yellow' : 'gray'}
        />
        <StatCard 
          label="Total Memory" 
          value={`${(totalMemory / 1024 / 1024).toFixed(0)}MB`} 
          color="gray"
        />
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 p-4 border-b border-gray-200">
          PM2 Applications
        </h3>
        <div className="divide-y divide-gray-200">
          {applications.map((app, index) => (
            <div key={index} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    app.status === 'online' ? 'bg-green-500' : 
                    app.status === 'stopping' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {app.name === 'iot-cloud' ? 'IoT Cloud' : 
                       app.name === 'dm2500i-api' ? 'Device API' :
                       app.name === 'nextjs-app' ? 'Marketing Website' : 
                       app.name}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {app.name === 'iot-cloud' ? 'iot.dryermaster.com' : 
                       app.name === 'dm2500i-api' ? 'app.dryermaster.com' :
                       app.name === 'nextjs-app' ? 'www.dryermaster.com' : 
                       ''}
                    </p>
                    <p className="text-sm text-gray-600">
                      Status: <span className={`font-medium ${
                        app.status === 'online' ? 'text-green-600' : 'text-red-600'
                      }`}>{app.status}</span>
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-8 text-sm">
                  <div className="text-center">
                    <p className="text-gray-500">CPU</p>
                    <p className="font-semibold">{app.cpu}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Memory</p>
                    <p className="font-semibold">{(app.memory / 1024 / 1024).toFixed(1)}MB</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Restarts</p>
                    <p className="font-semibold">{app.restarts}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Uptime</p>
                    <p className="font-semibold">
                      {app.uptime ? formatUptimeMs(Date.now() - app.uptime) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Chart */}
      {applications.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage by Application</h3>
          <div className="grid grid-cols-2 gap-6">
            {/* CPU Usage */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">CPU Usage (%)</h4>
              <div className="space-y-3">
                {applications.map((app, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">
                        {app.name === 'iot-cloud' ? 'IoT Cloud' : 
                         app.name === 'dm2500i-api' ? 'Device API' :
                         app.name === 'nextjs-app' ? 'Marketing Website' : 
                         app.name}
                      </span>
                      <span className="font-medium">{app.cpu}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          app.cpu > 80 ? 'bg-red-500' : 
                          app.cpu > 50 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(app.cpu, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Memory Usage */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Memory Usage (MB)</h4>
              <div className="space-y-3">
                {applications.map((app, index) => {
                  const memoryMB = app.memory / 1024 / 1024;
                  return (
                    <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                        {app.name === 'iot-cloud' ? 'IoT Cloud' : 
                         app.name === 'dm2500i-api' ? 'Device API' :
                         app.name === 'nextjs-app' ? 'Marketing Website' : 
                         app.name}
                      </span>
                        <span className="font-medium">{memoryMB.toFixed(1)}MB</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((memoryMB / 200) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Security Tab Component
const SecurityTab = ({ security }) => {
  const [loginFilter, setLoginFilter] = useState('all'); // all, today, week, month
  const [searchQuery, setSearchQuery] = useState('');

  // Format timestamp to user's local timezone
  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp === 'Active' || timestamp === 'Session ended') return timestamp;
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp; // Return as-is if invalid
      return format(date, 'EEE, MMM d, yyyy, hh:mm:ss a');
    } catch {
      return timestamp;
    }
  };

  if (!security) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <ShieldCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Security data not available</p>
        </div>
      </div>
    );
  }
  
  // Filter login history based on selected filter
  const getFilteredLogins = () => {
    if (!security.sshActivity?.recentLogins) return [];
    
    let filtered = [...security.sshActivity.recentLogins];
    const now = new Date();
    
    // Apply time filter
    switch (loginFilter) {
      case 'today':
        filtered = filtered.filter(login => {
          const loginDate = new Date(login.loginTime);
          return loginDate.toDateString() === now.toDateString();
        });
        break;
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(login => {
          const loginDate = new Date(login.loginTime);
          return loginDate >= weekAgo;
        });
        break;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(login => {
          const loginDate = new Date(login.loginTime);
          return loginDate >= monthAgo;
        });
        break;
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(login => 
        login.user.toLowerCase().includes(query) ||
        login.ip.toLowerCase().includes(query) ||
        login.terminal.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };
  
  const filteredLogins = getFilteredLogins();

  return (
    <div className="p-6 space-y-6 overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
      {/* Security Alerts */}
      {security.alerts && security.alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Security Alerts ({security.alerts.length})
              </h3>
              <div className="space-y-3">
                {security.alerts.map((alert, index) => (
                  <div key={index} className="bg-white border border-red-200 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-semibold ${
                        alert.severity === 'high' ? 'text-red-700' : 
                        alert.severity === 'medium' ? 'text-yellow-700' : 
                        'text-blue-700'
                      }`}>
                        {alert.severity?.toUpperCase()} SEVERITY
                      </span>
                      <span className="text-sm text-gray-600">{alert.type}</span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium mb-2">{alert.message}</p>
                    {alert.details && alert.details.length > 0 && (
                      <div className="text-xs text-gray-600 space-y-1">
                        <p className="font-medium">
                          Recent unauthorized access attempts ({alert.details.length} total):
                        </p>
                        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50">
                          {alert.details.map((detail, idx) => (
                            <p key={idx} className="pl-4 py-0.5 hover:bg-gray-100">
                              • {detail.user} from <span className="font-mono text-red-600">{detail.ip}</span> ({detail.loginTime})
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`rounded-lg p-4 ${security.firewallActive ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheckIcon className={`w-5 h-5 ${security.firewallActive ? 'text-green-600' : 'text-red-600'}`} />
            <p className="text-sm font-medium text-gray-700">Firewall</p>
          </div>
          <p className={`text-lg font-bold ${security.firewallActive ? 'text-green-700' : 'text-red-700'}`}>
            {security.firewallActive ? 'Active' : 'Inactive'}
          </p>
        </div>

        <div className={`rounded-lg p-4 ${security.fail2banActive ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <ShieldExclamationIcon className={`w-5 h-5 ${security.fail2banActive ? 'text-green-600' : 'text-yellow-600'}`} />
            <p className="text-sm font-medium text-gray-700">Fail2ban</p>
          </div>
          <p className={`text-lg font-bold ${security.fail2banActive ? 'text-green-700' : 'text-yellow-700'}`}>
            {security.fail2banActive ? 'Active' : 'Inactive'}
          </p>
        </div>

        <StatCard 
          label="SSH Sessions" 
          value={security.sshSessions} 
          color="blue"
        />

        <StatCard 
          label="Banned IPs" 
          value={security.bannedIPs} 
          color={security.bannedIPs > 0 ? 'yellow' : 'gray'}
        />
      </div>

      {/* SSH Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <UserIcon className="w-5 h-5 text-gray-600" />
          SSH Activity
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Active Now</h4>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-3xl font-bold text-blue-700">
                {security.sshActivity?.activeSessions?.length || security.sshSessions || 0}
              </p>
              <p className="text-sm text-blue-600 mt-1">users online</p>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Total Logins</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-3xl font-bold text-gray-900">{security.recentLogins}</p>
              <p className="text-sm text-gray-600 mt-1">recent attempts</p>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Unauthorized</h4>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-3xl font-bold text-red-700">
                {security.alerts?.find(a => a.type === 'unauthorized_access')?.details?.length || 0}
              </p>
              <p className="text-sm text-red-600 mt-1">suspicious logins</p>
            </div>
          </div>
        </div>
        
        {/* Active Sessions Details */}
        {security.sshActivity?.activeSessions?.length > 0 && (
          <div className="bg-green-50 rounded-lg p-3 mt-4">
            <h4 className="text-sm font-semibold text-green-800 mb-2">Currently Connected Users:</h4>
            <div className="space-y-1">
              {security.sshActivity.activeSessions.map((session, idx) => (
                <div key={idx} className="text-sm text-green-700 flex items-center justify-between">
                  <span>• {session.user} @ {session.terminal}</span>
                  <span className="font-mono text-xs">{session.ip}</span>
                  <span className="text-xs text-green-600">{session.loginTime}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detailed SSH Login History */}
      {security.sshActivity && security.sshActivity.recentLogins && security.sshActivity.recentLogins.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-gray-600" />
                SSH Login History ({filteredLogins.length} of {security.sshActivity.recentLogins.length} entries)
              </span>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Active:</span>
                <span className="font-bold text-green-600">
                  {filteredLogins.filter(l => l.isActive).length}
                </span>
                <span className="text-gray-500 ml-2">Unauthorized:</span>
                <span className="font-bold text-red-600">
                  {filteredLogins.filter(l => {
                    const isWhitelisted = security.ipConfiguration?.whitelistedIPs?.some(w => w.ip === l.ip);
                    return !isWhitelisted && l.ip !== 'local';
                  }).length}
                </span>
              </div>
            </h3>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Filter:</label>
                <select 
                  value={loginFilter} 
                  onChange={(e) => setLoginFilter(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </select>
              </div>
              
              <div className="flex-1 max-w-xs">
                <input
                  type="text"
                  placeholder="Search by user, IP, or terminal..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="text-sm text-gray-500">
                {filteredLogins.length === 0 && (
                  <span className="text-yellow-600">No matching entries found</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Terminal</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Time</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logout Time</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogins.map((login, idx) => {
                  const isWhitelisted = security.ipConfiguration?.whitelistedIPs?.some(w => w.ip === login.ip);
                  const isUnauthorized = !isWhitelisted && login.ip !== 'local';
                  
                  return (
                    <tr key={idx} className={
                      login.isActive ? 'bg-blue-50' : 
                      isUnauthorized ? 'bg-red-50' : ''
                    }>
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{login.user}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`font-mono ${isUnauthorized ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                          {login.ip}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">{login.terminal}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{formatTimestamp(login.loginTime)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {login.isActive ? (
                          <span className="text-green-600 font-medium">Active Now</span>
                        ) : formatTimestamp(login.logoutTime)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {login.isActive ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-1 animate-pulse" />
                            Online
                          </span>
                        ) : (
                          login.duration
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">
                        {isWhitelisted || login.ip === 'local' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="w-3 h-3 mr-1" />
                            Authorized
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircleIcon className="w-3 h-3 mr-1" />
                            Unauthorized
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-xs text-gray-500 text-center">
            Showing last 100 login attempts. Blue rows indicate active sessions, red rows indicate unauthorized access.
          </div>
        </div>
      )}

      {/* Pre-Authentication SSH Attempts */}
      {security.sshActivity?.preAuthAttempts && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldExclamationIcon className="w-5 h-5 text-yellow-600" />
            Pre-Authentication SSH Attempts
          </h3>
          
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-yellow-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Total Attempts</p>
              <p className="text-2xl font-bold text-yellow-700">
                {security.sshActivity.preAuthAttempts.summary.totalAttempts || 0}
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Unique IPs</p>
              <p className="text-2xl font-bold text-orange-700">
                {security.sshActivity.preAuthAttempts.summary.totalUniqueIPs || 0}
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Suspicious IPs</p>
              <p className="text-2xl font-bold text-red-700">
                {security.sshActivity.preAuthAttempts.byIP?.filter(ip => ip.totalAttempts > 5).length || 0}
              </p>
            </div>
          </div>
          
          {/* Top Offenders */}
          {security.sshActivity.preAuthAttempts.byIP && security.sshActivity.preAuthAttempts.byIP.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Top Suspicious IPs (Showing {Math.min(security.sshActivity.preAuthAttempts.byIP.length, 20)} of {security.sshActivity.preAuthAttempts.byIP.length})
              </h4>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempts</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempted Users</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Seen</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {security.sshActivity.preAuthAttempts.byIP.slice(0, 20).map((ipData, idx) => {
                      const isBlocked = security.ipConfiguration?.blockedIPs?.some(b => b.ip === ipData.ip) ||
                                       security.firewall?.rules?.some(r => r.from === ipData.ip && r.action === 'DENY');
                      const isBanned = security.fail2ban?.sshJail?.bannedIPs?.includes(ipData.ip);
                      const severity = ipData.totalAttempts > 20 ? 'high' : 
                                      ipData.totalAttempts > 10 ? 'medium' : 'low';
                      
                      return (
                        <tr key={idx} className={
                          severity === 'high' ? 'bg-red-50' :
                          severity === 'medium' ? 'bg-orange-50' :
                          'bg-yellow-50'
                        }>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`font-mono text-sm font-semibold ${
                              severity === 'high' ? 'text-red-700' :
                              severity === 'medium' ? 'text-orange-700' :
                              'text-yellow-700'
                            }`}>
                              {ipData.ip}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`text-sm font-bold ${
                              severity === 'high' ? 'text-red-600' :
                              severity === 'medium' ? 'text-orange-600' :
                              'text-yellow-600'
                            }`}>
                              {ipData.totalAttempts}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            <div className="max-w-xs truncate" title={ipData.attemptedUsers.join(', ')}>
                              {ipData.attemptedUsers.slice(0, 3).join(', ')}
                              {ipData.attemptedUsers.length > 3 && ` +${ipData.attemptedUsers.length - 3} more`}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                            {new Date(ipData.timeRange.first).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                            {new Date(ipData.timeRange.last).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isBlocked ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <LockClosedIcon className="w-3 h-3 mr-1" />
                                Blocked
                              </span>
                            ) : isBanned ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                <ShieldExclamationIcon className="w-3 h-3 mr-1" />
                                Banned
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                                Monitor
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Recent Attempt Details for Top Offender */}
              {security.sshActivity.preAuthAttempts.byIP[0] && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">
                    Recent attempts from {security.sshActivity.preAuthAttempts.byIP[0].ip}:
                  </h5>
                  <div className="space-y-1 text-xs text-gray-600 max-h-32 overflow-y-auto">
                    {security.sshActivity.preAuthAttempts.byIP[0].attempts.slice(-5).map((attempt, idx) => (
                      <div key={idx} className="font-mono">
                        {new Date(attempt.timestamp).toLocaleString()} - {attempt.type || attempt.reason}{attempt.port ? ` (port ${attempt.port})` : ''}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* IP Management */}
      <div className="grid grid-cols-2 gap-6">
        {/* Whitelisted IPs */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <LockClosedIcon className="w-5 h-5 text-green-600" />
              Whitelisted IPs
            </span>
            <span className="text-2xl font-bold text-green-700">{security.whitelistedIPs}</span>
          </h3>
          <p className="text-sm text-gray-600">
            Only these IPs are allowed to access the server without restrictions.
          </p>
        </div>

        {/* Blocked IPs */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ShieldExclamationIcon className="w-5 h-5 text-red-600" />
              Blocked IPs
            </span>
            <span className="text-2xl font-bold text-red-700">{security.blockedIPs}</span>
          </h3>
          <p className="text-sm text-gray-600">
            These IPs have been permanently blocked due to suspicious activity.
          </p>
        </div>
      </div>

      {/* Security Recommendations */}
      {(!security.firewallActive || !security.fail2banActive || security.alerts?.length > 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-3">Security Recommendations</h3>
          <ul className="space-y-2 text-sm text-yellow-700">
            {!security.firewallActive && (
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 mt-0.5" />
                <span>Enable firewall to protect against unauthorized access</span>
              </li>
            )}
            {!security.fail2banActive && (
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 mt-0.5" />
                <span>Enable fail2ban to automatically block brute force attempts</span>
              </li>
            )}
            {security.alerts?.length > 0 && (
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-4 h-4 mt-0.5" />
                <span>Review and address security alerts immediately</span>
              </li>
            )}
            <li className="flex items-start gap-2">
              <CheckCircleIcon className="w-4 h-4 mt-0.5" />
              <span>Regularly review SSH access logs and update whitelist</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

// Helper function for formatting uptime from milliseconds
const formatUptimeMs = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
};

// Network Tab Component
const NetworkTab = ({ system, aws }) => {
  const networkInterfaces = system?.network?.interfaces || {};
  const networkTraffic = system?.network?.traffic || {};
  const networkConnections = system?.network?.connections || {};
  const portUsage = system?.network?.portUsage || {};
  
  return (
    <div className="space-y-6">
      {/* Network Traffic Overview */}
      {Object.keys(networkTraffic).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Network Traffic</h3>
          <div className="space-y-4">
            {Object.entries(networkTraffic).map(([interfaceName, stats]) => (
              <div key={interfaceName} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <WifiIcon className="w-5 h-5 text-gray-400" />
                    {interfaceName}
                  </h4>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-blue-600">
                      <ArrowDownIcon className="w-4 h-4" />
                      {stats.rx_rate_formatted || '0 B/s'}
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      <ArrowUpIcon className="w-4 h-4" />
                      {stats.tx_rate_formatted || '0 B/s'}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Total Received</p>
                    <p className="text-sm font-medium text-gray-900">{stats.rx_formatted}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Total Sent</p>
                    <p className="text-sm font-medium text-gray-900">{stats.tx_formatted}</p>
                  </div>
                </div>
                
                {/* Traffic rate bars */}
                <div className="mt-3 space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Download Rate</span>
                      <span className="text-gray-900">{stats.rx_rate_formatted}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((stats.rx_rate || 0) / 1000000 * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Upload Rate</span>
                      <span className="text-gray-900">{stats.tx_rate_formatted}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((stats.tx_rate || 0) / 1000000 * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Network Connections */}
      {networkConnections.total > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Network Connections</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Total Connections</span>
                <span className="text-2xl font-bold text-gray-900">{networkConnections.total}</span>
              </div>
              
              {networkConnections.byState && (
                <div className="space-y-2">
                  {Object.entries(networkConnections.byState).map(([state, count]) => (
                    <div key={state} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 capitalize">{state.toLowerCase()}</span>
                      <span className={`text-sm font-medium ${
                        state === 'ESTAB' ? 'text-green-600' : 
                        state === 'LISTEN' ? 'text-blue-600' : 
                        state === 'TIME-WAIT' ? 'text-yellow-600' : 
                        'text-gray-600'
                      }`}>{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Port Usage */}
            {Object.keys(portUsage).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Active Ports</h4>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(portUsage).map(([port, count]) => (
                    <div key={port} className="bg-gray-50 rounded px-3 py-2 text-center">
                      <p className="text-xs text-gray-500">Port {port}</p>
                      <p className="text-sm font-medium text-gray-900">{count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Network Interfaces */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Network Interfaces</h3>
        {Object.keys(networkInterfaces).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(networkInterfaces).map(([name, interfaces]) => (
              <div key={name} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                  {name}
                </h4>
                {interfaces.map((iface, idx) => (
                  <div key={idx} className="text-sm text-gray-600 space-y-1">
                    {iface.family === 'IPv4' && (
                      <>
                        <p className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">IPv4:</span>
                          <span className="font-mono">{iface.address}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">MAC:</span>
                          <span className="font-mono text-xs">{iface.mac}</span>
                        </p>
                      </>
                    )}
                    {iface.family === 'IPv6' && (
                      <p className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">IPv6:</span>
                        <span className="font-mono text-xs truncate">{iface.address}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No network interfaces found</p>
        )}
      </div>

      {/* AWS Network Metrics */}
      {aws?.cloudWatch && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AWS CloudWatch Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <ArrowDownIcon className="w-8 h-8 text-blue-500 mx-auto" />
              <p className="mt-2 text-sm text-gray-600">Network In</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatBytes(aws.cloudWatch.network_in)}
              </p>
              <p className="text-xs text-gray-500">Last 5 minutes</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <ArrowUpIcon className="w-8 h-8 text-green-500 mx-auto" />
              <p className="mt-2 text-sm text-gray-600">Network Out</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatBytes(aws.cloudWatch.network_out)}
              </p>
              <p className="text-xs text-gray-500">Last 5 minutes</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const MetricCard = ({ title, value, icon: Icon, color = 'blue', trend, suffix = '%' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">{title}</p>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <p className="text-3xl font-bold text-gray-900">{value}{suffix}</p>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {trend > 0 ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <dt className="text-sm text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm font-medium text-gray-900">{value || 'N/A'}</dd>
  </div>
);

const StatCard = ({ label, value, color = 'gray' }) => {
  const colors = {
    gray: 'bg-gray-100 text-gray-900',
    green: 'bg-green-100 text-green-900',
    yellow: 'bg-yellow-100 text-yellow-900',
    blue: 'bg-blue-100 text-blue-900'
  };

  return (
    <div className={`rounded-lg p-4 ${colors[color]}`}>
      <p className="text-sm opacity-75">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

const MetricDisplay = ({ label, value, unit = '', icon: Icon }) => (
  <div className="text-center">
    {Icon && <Icon className="w-8 h-8 text-gray-400 mx-auto mb-2" />}
    <p className="text-sm text-gray-600">{label}</p>
    <p className="text-2xl font-semibold text-gray-900">{value}{unit}</p>
  </div>
);

const LoadBar = ({ label, value }) => {
  const percentage = Math.min(value * 100, 100);
  const color = percentage > 100 ? 'bg-red-500' : percentage > 80 ? 'bg-yellow-500' : 'bg-green-500';
  
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900">{value.toFixed(2)}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const ProgressBar = ({ value, label }) => (
  <div>
    <div className="flex justify-between text-sm mb-2">
      <span className="text-gray-600">{label}</span>
      <span className="text-gray-900">{value}%</span>
    </div>
    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
      <div 
        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
        style={{ width: `${value}%` }}
      />
    </div>
  </div>
);

const StatRow = ({ label, value, color = 'gray' }) => {
  const colors = {
    gray: 'text-gray-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600'
  };

  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-lg font-semibold ${colors[color]}`}>{value}</span>
    </div>
  );
};

const InstanceTypeInfo = ({ specifications }) => {
  // Use specifications from API if available, otherwise show N/A
  const spec = specifications || { vCPUs: 'N/A', memory: 'N/A', network: 'N/A' };
  
  // Format memory if it's a number
  const memoryDisplay = typeof spec.memory === 'number' ? `${spec.memory} GB` : spec.memory;

  return (
    <div className="grid grid-cols-3 gap-4">
      <StatCard label="vCPUs" value={spec.vCPUs} />
      <StatCard label="Memory" value={memoryDisplay} />
      <StatCard label="Network" value={spec.network} />
    </div>
  );
};

// Utility functions
const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatUptime = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  
  return parts.join(' ') || '0m';
};

// Add missing UserGroupIcon
const UserGroupIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

export default SystemHealthDetail;